import { createContext, useContext, useMemo, useState, ReactNode, useCallback, useEffect } from "react";
import { Dish } from "./components/DishCard";

export interface CartLine {
  key: string;
  dish: Dish;
  quantity: number;
  note?: string;
  sizeId?: string | null;
  addonIds?: string[];
}

interface CartState {
  tableNo: string | null;
  lines: CartLine[];
  totalItems: number;
  subtotal: number;
  tax: number;
  total: number;
  setTable: (no: string | null) => void;
  addItem: (line: Omit<CartLine, "quantity"> & { quantity?: number }) => void;
  setQuantity: (key: string, qty: number) => void;
  increment: (key: string) => void;
  decrement: (key: string) => void;
  remove: (key: string) => void;
  clear: () => void;
  setCartLines: (table: string, lines: CartLine[]) => void;
}

const CartContext = createContext<CartState | null>(null);
const TAX_RATE = 0.05;

const getCartLinePrice = (dish: Dish) => {
  const price = Number(dish.price || 0);
  const grossPrice = Number(dish.gross_price ?? price);
  return grossPrice > 0 && grossPrice < price ? grossPrice : price;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [tableNo, setTableNoState] = useState<string | null>(() => {
    return localStorage.getItem("active_table_no");
  });

  const [carts, setCarts] = useState<Record<string, CartLine[]>>(() => {
    try {
      const tenantId = localStorage.getItem("tenantId") || "default";
      const saved = localStorage.getItem(`waiter_carts_${tenantId}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const setTable = useCallback((no: string | null) => {
    setTableNoState(no);
    if (no) {
      localStorage.setItem("active_table_no", no);
    } else {
      localStorage.removeItem("active_table_no");
    }
  }, []);

  // Sync carts to localStorage whenever they change
  useEffect(() => {
    try {
      const tenantId = localStorage.getItem("tenantId") || "default";
      localStorage.setItem(`waiter_carts_${tenantId}`, JSON.stringify(carts));
    } catch (e) {
      console.error("Failed to save carts to localStorage", e);
    }
  }, [carts]);

  // Derived lines for current table
  const lines = useMemo(() => {
    if (!tableNo) return [];
    return carts[tableNo] || [];
  }, [carts, tableNo]);

  const addItem = useCallback<CartState["addItem"]>((line) => {
    if (!tableNo) return;
    setCarts((prev) => {
      const tableLines = prev[tableNo] || [];
      const existing = tableLines.find((l) => l.key === line.key);
      let newLines;
      if (existing) {
        newLines = tableLines.map((l) =>
          l.key === line.key ? { ...l, quantity: l.quantity + (line.quantity ?? 1) } : l
        );
      } else {
        newLines = [...tableLines, { ...line, quantity: line.quantity ?? 1 }];
      }
      return {
        ...prev,
        [tableNo]: newLines,
      };
    });
  }, [tableNo]);

  const setQuantity = useCallback((key: string, qty: number) => {
    if (!tableNo) return;
    setCarts((prev) => {
      const tableLines = prev[tableNo] || [];
      const newLines = qty <= 0
        ? tableLines.filter((l) => l.key !== key)
        : tableLines.map((l) => (l.key === key ? { ...l, quantity: qty } : l));
      return {
        ...prev,
        [tableNo]: newLines,
      };
    });
  }, [tableNo]);

  const increment = useCallback((key: string) => {
    if (!tableNo) return;
    setCarts((prev) => {
      const tableLines = prev[tableNo] || [];
      const newLines = tableLines.map((l) => (l.key === key ? { ...l, quantity: l.quantity + 1 } : l));
      return {
        ...prev,
        [tableNo]: newLines,
      };
    });
  }, [tableNo]);

  const decrement = useCallback((key: string) => {
    if (!tableNo) return;
    setCarts((prev) => {
      const tableLines = prev[tableNo] || [];
      const newLines = tableLines
        .map((l) => (l.key === key ? { ...l, quantity: l.quantity - 1 } : l))
        .filter((l) => l.quantity > 0);
      return {
        ...prev,
        [tableNo]: newLines,
      };
    });
  }, [tableNo]);

  const remove = useCallback((key: string) => {
    if (!tableNo) return;
    setCarts((prev) => {
      const tableLines = prev[tableNo] || [];
      const newLines = tableLines.filter((l) => l.key !== key);
      return {
        ...prev,
        [tableNo]: newLines,
      };
    });
  }, [tableNo]);

  const clear = useCallback(() => {
    if (!tableNo) return;
    setCarts((prev) => {
      const next = { ...prev };
      delete next[tableNo];
      return next;
    });
  }, [tableNo]);

  const setCartLines = useCallback((table: string, newLines: CartLine[]) => {
    setCarts((prev) => ({
      ...prev,
      [table]: newLines,
    }));
  }, []);

  const value = useMemo<CartState>(() => {
    const totalItems = lines.reduce((acc, l) => acc + l.quantity, 0);
    const subtotal = lines.reduce((acc, l) => acc + getCartLinePrice(l.dish) * l.quantity, 0);
    const tax = subtotal * TAX_RATE;
    const total = subtotal + tax;
    return {
      tableNo,
      lines,
      totalItems,
      subtotal,
      tax,
      total,
      setTable,
      addItem,
      setQuantity,
      increment,
      decrement,
      remove,
      clear,
      setCartLines,
    };
  }, [tableNo, lines, setTable, addItem, setQuantity, increment, decrement, remove, clear, setCartLines]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
};
